import { useEffect, useState } from "react";
import { BellRing, Download, MessagesSquare, Share, SquarePlus, X } from "lucide-react";
import { getInstallState, getManualInstallHint, isIOS, promptInstall, subscribeInstallState } from "../../services/pwa";
import { goInstallChatApp } from "../../services/chatApp";

// Only the chat app (IRG Chat, /chat) is installable. ERP pages invite people to get it; the chat page
// itself shows the browser's install prompt (or the steps, where there's no prompt).

const BANNER_DISMISSED_KEY = "irg-chat-install-banner-dismissed";

export function useInstallState() {
  const [state, setState] = useState(getInstallState);
  useEffect(() => subscribeInstallState(() => setState(getInstallState())), []);
  return state;
}

// Firefox can't install web apps at all; everywhere else the chat page can (prompt or browser menu).
export const canGetChatApp = () => !/Firefox/.test(window.navigator.userAgent);

const readDismissed = () => {
  try {
    return localStorage.getItem(BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};
const saveDismissed = () => {
  try {
    localStorage.setItem(BANNER_DISMISSED_KEY, "1");
  } catch {
    /* shown again next visit */
  }
};

// iPhone has no install prompt: people add the app from the Share sheet. Push notifications only
// work after that, so the steps say why it matters. Shown on the chat page, which is what gets added.
export function IosInstallSteps({ className = "" }) {
  const step = "flex items-start gap-3";
  const num = "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white";
  return (
    <ol className={`space-y-3 text-sm text-app-secondary ${className}`}>
      <li className={step}>
        <span className={num}>1</span>
        <span>
          Tap the <Share size={16} className="mx-0.5 inline -mt-1 text-blue-600" aria-label="Share" /> <b>Share</b> button in
          the browser bar.
        </span>
      </li>
      <li className={step}>
        <span className={num}>2</span>
        <span>
          Scroll down and tap <SquarePlus size={16} className="mx-0.5 inline -mt-1" aria-hidden="true" /> <b>Add to Home Screen</b>.
        </span>
      </li>
      <li className={step}>
        <span className={num}>3</span>
        <span>
          Open <b>IRG Chat</b> from your Home Screen and turn on chat notifications.
        </span>
      </li>
    </ol>
  );
}

function BannerShell({ icon: Icon, title, text, onDismiss, children, footer }) {
  return (
    <div className="mx-3 mt-3 rounded-2xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-500/30 dark:bg-blue-500/10" role="region" aria-label={title}>
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
          <Icon size={18} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-app-heading">{title}</p>
          <p className="mt-0.5 text-xs text-app-secondary">{text}</p>
          <div className="mt-2.5 flex flex-wrap gap-2">{children}</div>
        </div>
        {onDismiss && (
          <button type="button" onClick={onDismiss} aria-label="Dismiss" className="-m-1 rounded-full p-1 text-app-muted active:bg-app-hover">
            <X size={18} />
          </button>
        )}
      </div>
      {footer}
    </div>
  );
}

const primaryButton =
  "inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 active:bg-blue-700";
const quietButton = "rounded-lg px-3 py-1.5 text-xs font-medium text-app-secondary hover:bg-app-hover active:bg-app-hover";

// ERP pages on phones: a one-time card pointing to the chat app.
export function InstallBanner() {
  const { installed } = useInstallState();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed || installed || !canGetChatApp()) return null;

  const dismiss = () => {
    setDismissed(true);
    saveDismissed();
  };

  return (
    <BannerShell
      icon={MessagesSquare}
      title="Get the IRG Chat app"
      text="Team chat as its own app: full screen, on your Home Screen, with message and notice notifications."
      onDismiss={dismiss}
    >
      <button type="button" onClick={goInstallChatApp} className={primaryButton}>
        <Download size={14} aria-hidden="true" /> Get IRG Chat
      </button>
      <button type="button" onClick={dismiss} className={quietButton}>
        Not now
      </button>
    </BannerShell>
  );
}

// The chat page, in the browser: install IRG Chat. `force` (arriving from an ERP "Get IRG Chat" button)
// shows it even if it was dismissed before.
export function ChatInstallBanner({ force = false }) {
  const { canInstall, installed } = useInstallState();
  const ios = isIOS();
  const manualHint = getManualInstallHint();
  const [dismissed, setDismissed] = useState(() => !force && readDismissed());
  const [showSteps, setShowSteps] = useState(force && ios);

  if (dismissed || installed || (!canInstall && !ios && !manualHint)) return null;

  const dismiss = () => {
    setDismissed(true);
    saveDismissed();
  };

  return (
    <BannerShell
      icon={BellRing}
      title="Install IRG Chat"
      text={
        ios
          ? "Add it to your Home Screen to chat full screen and get message and notice notifications."
          : canInstall
            ? "Open chat like any other app, in its own window, with notifications."
            : manualHint
      }
      onDismiss={dismiss}
      footer={showSteps && <IosInstallSteps className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-500/30" />}
    >
      {canInstall && (
        <button type="button" onClick={promptInstall} className={primaryButton}>
          <Download size={14} aria-hidden="true" /> Install
        </button>
      )}
      {!canInstall && ios && (
        <button type="button" onClick={() => setShowSteps((v) => !v)} aria-expanded={showSteps} className={primaryButton}>
          {showSteps ? "Hide steps" : "Show me how"}
        </button>
      )}
      <button type="button" onClick={dismiss} className={quietButton}>
        Not now
      </button>
    </BannerShell>
  );
}
